import type { CaseStudy, Locale, ProofCardContent, ProofCardSpec } from '@/types/site'
import type { CaseStudy as CaseStudyDoc, Media, Post } from '@/payload-types'
import caseStudyPillars from '@/content/case-study-pillars.json'
import { canonicalCaseStudySlug } from '@/lib/case-study-slugs'

export const CALENDLY_URL = 'https://calendly.com/armando-kiranalabs/30min'
/**
 * The KAI Vault demo call. Until its own Calendly event exists it books the
 * same call; the page still tells the two apart (`?event=demo`), so bookings
 * are measured as `demo_booked`. Replace with the demo event's URL.
 */
export const CALENDLY_DEMO_URL = CALENDLY_URL
// embed_domain makes Calendly post its events (a booked call) to the page;
// lib/analytics.ts counts `calendly.event_scheduled` as a conversion.
export const CALENDLY_EMBED_URL = `${CALENDLY_URL}?embed_domain=1&embed_type=Inline`

/**
 * Calendly's embed colours for the dark theme (its `*_color` parameters, hex
 * without #): the dark card, its text and the dark accent. Calendly applies
 * them on paid plans; on a free plan the page stays in its default colours.
 */
const CALENDLY_DARK_COLORS = {
  background_color: '1b1815',
  text_color: 'ede8e2',
  primary_color: '8b81ff',
}

/** Campaign tags a scheduling link passes through to Calendly, which stores them on the booking. */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

/**
 * The scheduling embed in the site's theme. `search` is the scheduling
 * page's query string: `event=demo` books the KAI Vault demo instead of the
 * call, and its `utm_*` tags pass through to Calendly.
 */
export function calendlyEmbedUrl(theme: 'light' | 'dark', search = '') {
  const params = new URLSearchParams(search)
  const url = params.get('event') === 'demo' ? CALENDLY_DEMO_URL : CALENDLY_URL
  const query = new URLSearchParams({ embed_domain: '1', embed_type: 'Inline' })
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim()
    if (value) query.set(key, value.slice(0, 100))
  }
  if (theme === 'dark') for (const [key, value] of Object.entries(CALENDLY_DARK_COLORS)) query.set(key, value)
  return `${url}?${query}`
}

export function normalizePath(pathname: string) {
  try {
    return decodeURI(pathname)
  } catch {
    return null
  }
}

export function validPostSlug(value: string | null | undefined) {
  return Boolean(value && /^[\p{L}\p{N}][\p{L}\p{N}\p{M}’'_-]*$/u.test(value))
}

export function postPath(post: Pick<Post, 'locale' | 'slug'>) {
  return `${post.locale === 'es' ? '/es' : ''}/blog/${encodeURIComponent(post.slug)}`
}

export function validCaseStudySlug(value: string | null | undefined) {
  return Boolean(value && /^[\p{L}\p{N}][\p{L}\p{N}\p{M}’'()_-]*$/u.test(value))
}

/** A case study's address, at its canonical slug (lib/case-study-slugs). */
export function caseStudyPath(study: { locale?: string | null; slug?: string | null }) {
  const slug = encodeURIComponent(canonicalCaseStudySlug(study.slug || ''))
  return study.locale === 'es' ? `/es/caso-de-estudio/${slug}` : `/case-study/${slug}`
}

/** Authenticated live-preview route for a collection draft (see app/(platform)/[locale]/preview/). */
export function previewPath(
  collection: 'posts' | 'case-studies',
  doc: { locale?: string | null; slug?: string | null },
) {
  const prefix = doc?.locale === 'es' ? '/es' : ''
  return `${prefix}/preview/${collection}/${encodeURIComponent(doc?.slug || '')}`
}

export function navigation(locale: Locale) {
  return locale === 'es'
    ? [
        { labelKey: 'newBusiness' as const, href: '/es/servicios/nuevo-negocio' },
        { labelKey: 'digitalEnterprise' as const, href: '/es/servicios/empresa' },
        { labelKey: 'aiSolutions' as const, href: '/es/servicios/ia' },
        { labelKey: 'latamNearshoring' as const, href: '/es/servicios/latam' },
        { labelKey: 'caseStudies' as const, href: '/es/casos-de-estudio' },
        { labelKey: 'blog' as const, href: '/es/blog' },
      ]
    : [
        { labelKey: 'newBusiness' as const, href: '/services/new-business' },
        { labelKey: 'digitalEnterprise' as const, href: '/services/enterprise' },
        { labelKey: 'aiSolutions' as const, href: '/services/ai' },
        { labelKey: 'latamNearshoring' as const, href: '/services/latam' },
        { labelKey: 'caseStudies' as const, href: '/Case-Studies' },
        { labelKey: 'blog' as const, href: '/blog' },
      ]
}

export function coverImage(post: Pick<Post, 'coverImage'>) {
  return mediaUrl(post.coverImage)
}

/** Resolves a populated upload field (Payload depth ≥ 1) to its file URL. */
export function mediaUrl(media: unknown): string {
  return typeof media === 'object' && media !== null && typeof (media as Media).url === 'string'
    ? (media as Media).url!
    : ''
}

/** Adapts a published `case-studies` document to the shared card model. */
export function caseStudyCardModel(
  study: Pick<
    CaseStudyDoc,
    'client' | 'title' | 'excerpt' | 'slug' | 'locale' | 'categories' | 'cardImage' | 'coverImage'
  >,
): CaseStudy {
  const tags = (study.categories || []).map((category) => category.name)
  const href = caseStudyPath(study)
  return {
    company: study.client,
    tags: { en: tags, es: tags },
    summary: { en: study.title, es: study.title },
    description: { en: study.excerpt, es: study.excerpt },
    image: mediaUrl(study.cardImage) || mediaUrl(study.coverImage),
    href: { en: href, es: href },
  }
}

/**
 * Resolves a v2 proof card against the published case studies: a card
 * whose `slug` matches a study links to it and borrows its card image;
 * placeholder cards (no slug, or not published) render unlinked.
 */
export function proofCardModel(
  spec: ProofCardSpec,
  studies: Pick<CaseStudyDoc, 'slug' | 'locale' | 'cardImage' | 'coverImage'>[],
): ProofCardContent {
  const { slug, ...card } = spec
  // An exact slug first, then the same case study under an alias (g2x-copy).
  const study = slug
    ? (studies.find((s) => s.slug === slug) ??
      studies.find((s) => canonicalCaseStudySlug(s.slug) === canonicalCaseStudySlug(slug)))
    : undefined
  if (!study) return card
  return {
    ...card,
    href: caseStudyPath(study),
    image: mediaUrl(study.cardImage) || mediaUrl(study.coverImage) || undefined,
  }
}

export type PillarKey = 'new-business' | 'enterprise' | 'ai' | 'embedded'

/** The four ways to work with Kirana, in navigation order, per locale. */
export const PILLARS: Record<Locale, Record<PillarKey, string>> = {
  en: {
    'new-business': 'New Business',
    enterprise: 'Digital Enterprise',
    ai: 'KAI Vault',
    embedded: 'Embedded Product Teams',
  },
  es: {
    'new-business': 'Nuevo Negocio',
    enterprise: 'Empresa Digital',
    ai: 'KAI Vault',
    embedded: 'Equipos de Producto Integrados',
  },
}

type PillarEntry = { pillar: PillarKey; stage?: Record<Locale, string> }

/**
 * Localized pillar and stage/bottleneck tags for a case study slug, from
 * content/case-study-pillars.json (keyed by both English and Spanish slugs).
 */
export function caseStudyTags(slug: string, locale: Locale): { pillar?: string; stage?: string } {
  const entry = (caseStudyPillars as Record<string, PillarEntry>)[canonicalCaseStudySlug(slug)]
  if (!entry) return {}
  return { pillar: PILLARS[locale][entry.pillar], stage: entry.stage?.[locale] }
}

/**
 * Listing entry for a published case study: v2 proof card plus its pillar
 * and industry categories. The result line is the case's published outcomes
 * (`caseStudyOutcomes`); an unmapped tag or missing result is left out.
 */
export function caseStudyFilterModel(
  study: Pick<
    CaseStudyDoc,
    'client' | 'excerpt' | 'slug' | 'locale' | 'categories' | 'cardImage' | 'coverImage'
  >,
  result?: string,
) {
  const { pillar, stage } = caseStudyTags(study.slug, study.locale)
  return {
    card: {
      company: study.client,
      tags: [pillar, stage].filter((tag): tag is string => Boolean(tag)),
      body: study.excerpt,
      result,
      href: caseStudyPath(study),
      image: mediaUrl(study.cardImage) || mediaUrl(study.coverImage) || undefined,
    },
    pillar,
    industries: (study.categories || []).map((category) => category.name),
  }
}

/** Case-insensitive, accent-insensitive key for matching industry names. */
function industryKey(name: string) {
  return name.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase()
}

/** Industry links show only once this many industries have a case study. */
export const MIN_LINKED_INDUSTRIES = 2

/**
 * Homepage industry index: each industry links to the most recent
 * published case study (studies arrive sorted newest first) that lists it
 * among its categories, so the proof comes from the CMS and a cell with
 * no matching study simply shows no link. While fewer than
 * MIN_LINKED_INDUSTRIES industries have one, no cell links: a single link
 * in the grid reads as a gap rather than as proof.
 */
export function industryIndex(
  industries: string[],
  studies: Pick<CaseStudyDoc, 'client' | 'slug' | 'locale' | 'categories'>[],
): { name: string; caseStudy?: { client: string; href: string } }[] {
  const cells = industries.map((name) => {
    const key = industryKey(name)
    const study = studies.find((s) =>
      (s.categories || []).some((category) => industryKey(category.name) === key),
    )
    return study
      ? { name, caseStudy: { client: study.client, href: caseStudyPath(study) } }
      : { name }
  })
  const linked = cells.filter((cell) => 'caseStudy' in cell).length
  return linked >= MIN_LINKED_INDUSTRIES ? cells : industries.map((name) => ({ name }))
}
